import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import { ApiError, useApi } from "./api";
import type { AuthedUser } from "./types";

export type AccessStatus = "accepted" | "pending";

interface InternalState {
  user: AuthedUser | null;
  accessStatus: AccessStatus | null;
  loading: boolean;
  error: string | null;
}

export interface UseCurrentUserResult extends InternalState {
  refetch: () => void;
}

const ME_TIMEOUT_MS = 10_000;
const INITIAL: InternalState = {
  user: null,
  accessStatus: null,
  loading: true,
  error: null,
};

function sameState(a: InternalState, b: InternalState) {
  return (
    a.user === b.user &&
    a.accessStatus === b.accessStatus &&
    a.loading === b.loading &&
    a.error === b.error
  );
}

export function useCurrentUser(): UseCurrentUserResult {
  const { isLoaded, isSignedIn } = useAuth();
  const api = useApi();

  // Hold the api callable in a ref so the fetch effect's dep array stays
  // free of object identities that could change render-to-render. Without
  // this, an unstable api reference would refire the effect on every render
  // and the unconditional setState calls below would create a render loop.
  const apiGetRef = useRef(api.get);
  useEffect(() => {
    apiGetRef.current = api.get;
  }, [api.get]);

  const [state, setStateRaw] = useState<InternalState>(INITIAL);
  const [tick, setTick] = useState(0);

  const setState = useCallback((next: InternalState) => {
    setStateRaw((prev) => (sameState(prev, next) ? prev : next));
  }, []);

  const refetch = useCallback(() => {
    setTick((t) => t + 1);
  }, []);

  useEffect(() => {
    if (!isLoaded) {
      const t = setTimeout(() => {
        setStateRaw((s) =>
          s.loading
            ? {
                user: null,
                accessStatus: null,
                loading: false,
                error:
                  "Auth provider didn't load. Check your network and Clerk publishable key, then reload.",
              }
            : s,
        );
      }, ME_TIMEOUT_MS);
      return () => clearTimeout(t);
    }

    if (!isSignedIn) {
      setState({
        user: null,
        accessStatus: null,
        loading: false,
        error: null,
      });
      return;
    }

    let settled = false;
    const ac = new AbortController();
    setState({
      user: null,
      accessStatus: null,
      loading: true,
      error: null,
    });

    const timeout = setTimeout(() => {
      if (settled) return;
      settled = true;
      ac.abort();
      setStateRaw((s) =>
        s.loading
          ? {
              user: null,
              accessStatus: null,
              loading: false,
              error:
                "Timed out loading your profile. The /api/me endpoint may be unreachable. Is `npm run pages:dev` running?",
            }
          : s,
      );
    }, ME_TIMEOUT_MS);

    apiGetRef
      .current<{ user: AuthedUser; access_status?: AccessStatus }>("/api/me")
      .then((res) => {
        if (settled || ac.signal.aborted) return;
        settled = true;
        clearTimeout(timeout);
        if (!res?.user) {
          setState({
            user: null,
            accessStatus: null,
            loading: false,
            error: null,
          });
          return;
        }
        setState({
          user: res.user,
          accessStatus: res.access_status ?? "pending",
          loading: false,
          error: null,
        });
      })
      .catch((err: unknown) => {
        if (settled || ac.signal.aborted) return;
        settled = true;
        clearTimeout(timeout);
        // 401 means signed-in-with-Clerk but no profile on our side, or a
        // race where the session ended mid-fetch. Surface it as a stable
        // "no profile yet" state instead of a fatal error.
        if (err instanceof ApiError && err.status === 401) {
          setState({
            user: null,
            accessStatus: null,
            loading: false,
            error: null,
          });
          return;
        }
        const message =
          err instanceof Error ? err.message : "Could not load your profile.";
        setState({
          user: null,
          accessStatus: null,
          loading: false,
          error: message,
        });
      });

    return () => {
      settled = true;
      ac.abort();
      clearTimeout(timeout);
    };
  }, [isLoaded, isSignedIn, tick, setState]);

  return useMemo(() => ({ ...state, refetch }), [state, refetch]);
}
